import { SearchProvider, SearchProviderType } from '@/types/searchProvider';
import { SolutionCardProps } from '@/components/SolutionCard';
import { StandardSearchProvider } from './StandardSearchProvider';

export class N8nAISearchProvider implements SearchProvider {
  private standardProvider = new StandardSearchProvider();
  
  getProviderType(): SearchProviderType {
    return 'n8n-search-hybrid-standard-ai';
  }
  
  getExpectedResponseTime(): number {
    return 3000; // 3s expected response time for AI processing
  }
  
  isNaturalLanguageSupported(): boolean {
    return true; // AI provider supports natural language
  }
  
  getDisplayName(): string {
    return 'Hybrid AI + Standard Search';
  }
  
  async search(query: string): Promise<SolutionCardProps[]> {
    console.log(`Hybrid search called with query: "${query}"`);
    
    // Start both searches in parallel for optimal performance
    const standardSearchPromise = this.getStandardResults(query);
    const aiSearchPromise = this.getAIResults(query);
    
    try {
      // Wait for both searches to complete
      const [standardResults, aiResults] = await Promise.allSettled([
        standardSearchPromise,
        aiSearchPromise
      ]);
      
      // Combine results with AI results taking priority
      const combinedResults = this.combineResults(standardResults, aiResults);
      
      console.log(`Hybrid search completed: ${combinedResults.length} total results`);
      return combinedResults;
      
    } catch (error) {
      console.error('Hybrid search failed:', error);
      
      // Fallback to standard search if available
      try {
        const fallbackResults = await standardSearchPromise;
        console.log(`Fallback to standard search: ${fallbackResults.length} results`);
        return fallbackResults;
      } catch (fallbackError) {
        console.error('Fallback search also failed:', fallbackError);
        throw new Error(`Search temporarily unavailable.`);
      }
    }
  }
  
  /**
   * Get standard search results (fast, traditional PostgreSQL search)
   */
  private async getStandardResults(query: string): Promise<SolutionCardProps[]> {
    try {
      const results = await this.standardProvider.search(query);
      
      // Mark standard results for identification
      return results.map(result => ({
        ...result,
        search_type: 'standard' as const
      }));
    } catch (error) {
      console.warn('Standard search failed:', error);
      return [];
    }
  }
  
  /**
   * Get AI search results (slower, semantic understanding)
   */
  private async getAIResults(query: string): Promise<SolutionCardProps[]> {
    try {
      // Get n8n webhook URL from environment or use production instance
      const n8nWebhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://nn.simpletechx.com/webhook/ai-search';
      
      console.log(`Calling AI search webhook: ${n8nWebhookUrl}`);
      
      // Call n8n AI Search workflow
      const response = await fetch(n8nWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query
        })
      });
      
      if (!response.ok) {
        throw new Error(`AI webhook failed: ${response.status} ${response.statusText}`);
      }
      
      // Check if response has content
      const responseText = await response.text();
      
      if (!responseText.trim()) {
        throw new Error('AI webhook returned empty response');
      }
      
      const data = JSON.parse(responseText);
      
      // Handle both single object and array responses from n8n
      const results = Array.isArray(data) ? data : [data];
      console.log(`AI search: ${results.length} results`);
      
      // Mark AI results for identification
      return results.map((result: any) => ({
        ...result,
        search_type: 'ai_vector' as const
      }));
      
    } catch (error) {
      console.warn('AI search failed:', error);
      return [];
    }
  }
  
  /**
   * Combine and deduplicate results, prioritizing AI results for relevance
   */
  private combineResults(
    standardResults: PromiseSettledResult<SolutionCardProps[]>,
    aiResults: PromiseSettledResult<SolutionCardProps[]>
  ): SolutionCardProps[] {
    const standard = standardResults.status === 'fulfilled' ? standardResults.value : [];
    const ai = aiResults.status === 'fulfilled' ? aiResults.value : [];
    
    console.log(`Combining results: ${ai.length} AI + ${standard.length} standard`);
    
    // If we have AI results, prioritize them
    if (ai.length > 0) {
      // Remove standard results that already appear in AI results (deduplicate by ID)
      const aiIds = new Set(ai.map(result => result.id));
      const uniqueStandard = standard.filter(result => !aiIds.has(result.id));
      
      // Return AI results first, then additional standard results
      const combined = [...ai, ...uniqueStandard];
      console.log(`Hybrid results: ${ai.length} AI priority + ${uniqueStandard.length} additional standard`);
      return combined;
    }
    
    // Fallback to standard results if no AI results
    console.log(`Fallback to standard results: ${standard.length} total`);
    return standard;
  }
} 